import { WorkOrderKind, WorkOrderSortKey } from '@atlas/types'
import { SortDirection } from '@atlas/ui-components'
import type { WorkOrderListParams } from '@/services/work-order.service'

export const WORK_ORDER_PAGE_SIZE = 20

/** The list an order is found on: a factory brief on the briefs page, an asset brief on the work-order list. */
export const listPathFor = (kind: WorkOrderKind): string =>
	kind === WorkOrderKind.FACTORY_BRIEF ? '/work-orders/briefs' : '/work-orders'

/** Whose orders the list shows. */
export enum ListScope {
	MINE = 'mine',
	ALL = 'all',
}

/** The list opens on the caller's own orders unless the URL names a scope. */
export const DEFAULT_SCOPE = ListScope.MINE

/** Everything the list view is showing, as held in the URL. */
export interface WorkOrderListState {
	scope: ListScope
	q: string
	brand: string[]
	assignee: string[]
	status: string[]
	sort: WorkOrderSortKey | null
	order: SortDirection
	page: number
}

const SORT_KEYS = new Set<string>(Object.values(WorkOrderSortKey))
const listOf = (value: string | null) => (value ? value.split(',').filter(Boolean) : [])

/** Read the list state from the query string; the scope defaults to My tasks when the URL names none. */
export function readListState(params: URLSearchParams): WorkOrderListState {
	const scope = params.get('scope')
	const sort = params.get('sort')
	const page = Number(params.get('page'))
	return {
		scope: scope === ListScope.MINE || scope === ListScope.ALL ? scope : DEFAULT_SCOPE,
		q: params.get('q') ?? '',
		brand: listOf(params.get('brand')),
		assignee: listOf(params.get('assignee')),
		status: listOf(params.get('status')),
		sort: sort && SORT_KEYS.has(sort) ? (sort as WorkOrderSortKey) : null,
		order: params.get('order') === SortDirection.ASC ? SortDirection.ASC : SortDirection.DESC,
		page: Number.isInteger(page) && page > 1 ? page : 1,
	}
}

/**
 * The query string after applying `patch`. Any change other than the page itself
 * returns to page 1, since the old page number means nothing in a new result.
 * Empty values are dropped so the URL stays short.
 */
export function writeListState(current: WorkOrderListState, patch: Partial<WorkOrderListState>): URLSearchParams {
	const next = { ...current, ...('page' in patch ? {} : { page: 1 }), ...patch }
	const entries: Array<[string, string]> = [
		['scope', next.scope],
		['q', next.q],
		['brand', next.brand.join(',')],
		['assignee', next.assignee.join(',')],
		['status', next.status.join(',')],
		['sort', next.sort ?? ''],
		['order', next.sort ? next.order : ''],
		['page', next.page > 1 ? String(next.page) : ''],
	]
	return new URLSearchParams(entries.filter(([, value]) => value !== ''))
}

/** Header click: a new column sorts ascending, the sorted column flips direction. */
export function nextSort(
	state: Pick<WorkOrderListState, 'sort' | 'order'>,
	key: WorkOrderSortKey,
): Pick<WorkOrderListState, 'sort' | 'order'> {
	if (state.sort !== key) return { sort: key, order: SortDirection.ASC }
	return { sort: key, order: state.order === SortDirection.ASC ? SortDirection.DESC : SortDirection.ASC }
}

/**
 * The `GET /work-orders` parameters for a state. The work-order list shows the
 * asset briefs, and the factory briefs list (`briefs`) the factory briefs.
 */
export function toListParams(state: WorkOrderListState, briefs = false): WorkOrderListParams {
	const joined = (values: string[]) => (values.length ? values.join(',') : undefined)
	return {
		q: state.q.trim() || undefined,
		mine: state.scope === ListScope.MINE || undefined,
		kind: briefs ? WorkOrderKind.FACTORY_BRIEF : WorkOrderKind.ASSET_BRIEF,
		'filters[brand]': joined(state.brand),
		'filters[assignee]': joined(state.assignee),
		'filters[status]': joined(state.status),
		sort: state.sort ?? undefined,
		order: state.sort ? state.order : undefined,
		page: state.page,
		limit: WORK_ORDER_PAGE_SIZE,
	}
}
